import { IconButton } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { CardContent, Grid, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useEntitlementActive } from '../../../../hooks/use-entitlement-active';
import { ExpandedGroupContent } from '../expanded-group-content';
import * as Styled from './styles';
import * as SX from './sx';
import type { SubscriptionRowProps as Props } from './types';

export const SubscriptionRow = memo(
  ({
    className,
    row,
    rowIndex,
    expanded,
    onToggleExpand,
    onToggleIcon,
    expandedIcons,
    isExpanded,
  }: Props) => {
    const [isGroupActiveInFree, setIsGroupActiveInFree] =
      useState<boolean>(false);
    const [isGroupActiveInPaid, setIsGroupActiveInPaid] =
      useState<boolean>(false);

    const { isActiveInFree, isActiveInPaid } = useEntitlementActive();

    const groupedEntitlment = 'groups' in row;

    const productIds = groupedEntitlment ? [] : row.products;

    useEffect(() => {
      if (groupedEntitlment) {
        setIsGroupActiveInFree(
          row.groups.some((i) =>
            i.entitlements.some((g) => isActiveInFree(g.products))
          )
        );
        setIsGroupActiveInPaid(
          row.groups.some((i) =>
            i.entitlements.some((g) => isActiveInPaid(g.products))
          )
        );
      }
    }, [row]);

    const isFreeActive = groupedEntitlment
      ? isGroupActiveInFree
      : isActiveInFree(row.products);

    const isPaidActive = groupedEntitlment
      ? isGroupActiveInPaid
      : isActiveInPaid(row.products);

    return (
      <Styled.Wrapper
        className={classnames('SubscriptionRow', className)}
        sx={SX.root(!!expanded)}
        variant="outlined"
      >
        <CardContent padding="normal" sx={SX.cardContent}>
          <Grid container sx={SX.row}>
            <Grid item xs={6} sx={SX.grid}>
              <Typography variant="body2" color="primary.900" fontWeight="700">
                {groupedEntitlment ? row.parent : row.name}
              </Typography>
            </Grid>
            <Grid item xs={2.5} sx={SX.grid}>
              <Typography variant="body3" color="primary.900" fontWeight="700">
                Free
              </Typography>
            </Grid>
            <Grid item xs={3.5} sx={SX.grid}>
              <Typography variant="body3" color="primary.900" fontWeight="700">
                Premium Access
              </Typography>
              {groupedEntitlment && (
                <IconButton
                  icon={expanded ? 'ios-chevron-up' : 'ios-chevron-down'}
                  size="small"
                  variant="outlined"
                  iconSize={20}
                  color={expanded ? 'primary' : 'default'}
                  onClick={() => onToggleExpand(row.parent)}
                />
              )}
            </Grid>
          </Grid>
          {expanded && groupedEntitlment && row.groups && (
            <ExpandedGroupContent
              details={row.groups}
              rowIndex={rowIndex}
              onToggleIcon={onToggleIcon}
              expandedIcons={expandedIcons}
            />
          )}
        </CardContent>
      </Styled.Wrapper>
    );
  }
);

SubscriptionRow.displayName = 'SubscriptionRow';
